import { ApiResponse, HttpClient } from '@store/base';
import {
  AddCartGoodsGateway,
  BuyDirectGoodsGateway,
  CreateEstimateCertificationConfirmGateway,
  CreateEstimateCertificationGateway,
  GetOnlineEstimateDetailGuestGateway,
  GetOnlineEstimateDetailGuestRequest,
  GetOnlineEstimateDetailMemberGateway,
  GetOnlineEstimateDetailMemberRequest,
  GetOnlineEstimateGuestGateway,
  GetOnlineEstimateMemberGateway,
  OnlineEstimateDetail,
  UpdateOnlineEstimateOrderStatusGateway,
} from '@store/order';

import { axios } from '@/di-container/axios';
import { ClientGetEstimateNotificationImagesGateway } from './get-estimate-notification-images';
import { ClientGetEstimateMaterialNoticesGateway } from './get-estimate-material-notices';
import {
  ClientCancelAsCareGateway,
  ClientCompleteAsCareApplyGateway,
  ClientGetAsCareApplySheetGateway,
  ClientGetAsCareCancelListGateway,
  ClientGetAsCareListGateway,
  ClientGetAsCarePayRequestGateway,
} from './as-care';

// ── OFFBEN-83: 프레젠테이션 뷰모델 + 매핑 (레이어 분리) ───────────────
// shared 게이트웨이 타입(OrderBenefit/PayCouponVer2)을 뷰가 직접 쓰지 않도록
// di-container에서 클라이언트 뷰모델로 변환해 내려준다.

// 결제할인 표시용 뷰모델 (PayCouponVer2 → 평탄화, 뷰가 쓰는 필드만)
export interface EstimatePaymentDiscount {
  payCouponStep: number;
  promoNm: string;
  totalApplyAmt: number;
}

// 견적 혜택 요약 뷰모델 (OrderBenefit 중 뷰가 쓰는 필드)
export interface EstimateBenefitView {
  totalNormalSalePrc: number;
  totalDcAmt: number;
  totalCouponAmt: number;
  totalOrderPrc: number;
  totalBenefitAmt: number;
  useAbleEndYmdt: string;
}

// 상세 응답 클라이언트 타입: 원본 + 정규화된 결제할인(paymentDiscounts)
export interface ClientOnlineEstimateDetail extends OnlineEstimateDetail {
  paymentDiscounts: EstimatePaymentDiscount[];
}

export interface ClientGetOnlineEstimateDetailMemberResponse
  extends ApiResponse<ClientOnlineEstimateDetail> {}
export interface ClientGetOnlineEstimateDetailGuestResponse
  extends ApiResponse<ClientOnlineEstimateDetail> {}

// version에 따라 결제할인 정규화: v2=payCoupons(복수) / v1=payCoupon(단수)→0·1개.
// 0개 판별은 orderBenefit.applyPayCouponCount로 (payCoupon 형태 무관 안전).
const toPaymentDiscounts = (
  detail: OnlineEstimateDetail,
): EstimatePaymentDiscount[] => {
  if (detail.version === 2) {
    return (detail.payCoupons ?? []).map((pc) => ({
      payCouponStep: pc.payCouponStep,
      promoNm: pc.appliedCoupon.promoNm,
      totalApplyAmt: pc.appliedCoupon.totalApplyAmt,
    }));
  }
  const { payCoupon, orderBenefit } = detail;
  const hasPayCoupon =
    (orderBenefit?.applyPayCouponCount ?? 0) > 0 && !!payCoupon?.promoNo;
  return hasPayCoupon
    ? [
        {
          payCouponStep: 1,
          promoNm: payCoupon?.promoNm ?? '',
          totalApplyAmt: payCoupon?.totalApplyAmt ?? 0,
        },
      ]
    : [];
};

const withPaymentDiscounts = (
  res: ApiResponse<OnlineEstimateDetail>,
): ApiResponse<ClientOnlineEstimateDetail> => ({
  ...res,
  data: res.data
    ? { ...res.data, paymentDiscounts: toPaymentDiscounts(res.data) }
    : undefined,
});

export class ClientGetOnlineEstimateDetailMemberGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(
    req: GetOnlineEstimateDetailMemberRequest,
  ): Promise<ClientGetOnlineEstimateDetailMemberResponse> {
    const res = await new GetOnlineEstimateDetailMemberGateway(
      this.httpClient,
    ).exec(req);
    return withPaymentDiscounts(res);
  }
}

export class ClientGetOnlineEstimateDetailGuestGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(
    req: GetOnlineEstimateDetailGuestRequest,
  ): Promise<ClientGetOnlineEstimateDetailGuestResponse> {
    const res = await new GetOnlineEstimateDetailGuestGateway(
      this.httpClient,
    ).exec(req);
    return withPaymentDiscounts(res);
  }
}

export const orderGateway = () => {
  return {
    addCartGoods: new AddCartGoodsGateway(axios),
    buyDirectGoods: new BuyDirectGoodsGateway(axios),
  };
};

// OFFBEN-83 — 목록/상세 v3(version 인지)로 전환. 인증/주문상태는 v1 유지.
export const onlineEstimateGateway = () => {
  return {
    member: new GetOnlineEstimateMemberGateway(axios),
    guest: new GetOnlineEstimateGuestGateway(axios),
    memberDetail: new ClientGetOnlineEstimateDetailMemberGateway(axios),
    guestDetail: new ClientGetOnlineEstimateDetailGuestGateway(axios),
    certification: new CreateEstimateCertificationGateway(axios),
    certificationConfirm: new CreateEstimateCertificationConfirmGateway(axios),
    updateStatus: new UpdateOnlineEstimateOrderStatusGateway(axios),
    // HSMALL-15407 — 견적 알림톡 참고이미지 조회 (비로그인 토큰 기반)
    notificationImages: new ClientGetEstimateNotificationImagesGateway(axios),
    // SALES-14036 — 소재고지 알림톡 고지내역 조회 (비로그인 토큰 기반)
    materialNotices: new ClientGetEstimateMaterialNoticesGateway(axios),
  };
};

/**
 * 한샘 AS케어+ (연장보증보험) — SC-02 · SC-03 · SC-04 · SC-05 · SC-07 · SC-08.
 * 대상 회원은 토큰으로 특정되므로 목록 조회에는 파라미터가 없다.
 */
export const asCareGateway = () => {
  return {
    /** SC-02 보험신청/내역 */
    list: new ClientGetAsCareListGateway(axios),
    /** SC-03 보험신청서 */
    applySheet: new ClientGetAsCareApplySheetGateway(axios),
    /** SC-04 Step 2 결제요청 파라미터 */
    payRequest: new ClientGetAsCarePayRequestGateway(axios),
    /** SC-05 신청 확정 (PG 인증결과 중계) */
    applyComplete: new ClientCompleteAsCareApplyGateway(axios),
    /** SC-07 취소 접수 */
    cancel: new ClientCancelAsCareGateway(axios),
    /** SC-08 보험취소/내역 */
    cancelList: new ClientGetAsCareCancelListGateway(axios),
  };
};
